"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, Eye, ChartLine as LineChart } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const steps = [{
  number: "01",
  title: "Non-Invasive Optical Interrogation",
  summary: "Direct measurement through container walls without compromising sample integrity or requiring mechanical contact.",
  detail: "Precision optics interface with standard process vessels and transfer lines, transmitting light through dedicated sight glasses or custom flow cells.",
  icon: "Eye"
}, {
  number: "02",
  title: "Continuous Spectral Acquisition",
  summary: "High-speed data collection delivering continuous diagnostic metrics across production cycles.",
  detail: "Multi-channel optical sensors capture real-time absorption and reflectance data to monitor critical chemical and physical parameters.",
  icon: "Activity"
}, {
  number: "03",
  title: "Dynamic Moisture Trajectory Tracking",
  summary: "Algorithmic endpoint detection and real-time drying curves tailored for regulated processing.",
  detail: "Automated trend mapping and threshold monitoring provide immediate feedback for batch validation and process optimization.",
  icon: "LineChart"
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="threestepprocess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-2xl mb-16 lg:mb-20">
          <Badge variant="outline" className="border-primary/20 text-primary bg-primary/10 mb-4 px-3 py-1 font-semibold">
            Process Workflow
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
            Three-Step Analytical Architecture
          </h2>
          <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
            Our non-invasive process analytical workflow provides real-time verification and automated trajectory control for regulated pharmaceutical and chemical manufacturing.
          </p>
        </div>

        {/* Steps Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {steps.map((step, i) => <motion.div key={step.number} data-index={i} variants={itemVariants} className="flex">
              <Card className="flex flex-col justify-between w-full border-border bg-card shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                <div className="absolute top-0 left-0 right-0 h-1 bg-muted group-hover:bg-primary transition-colors" />

                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <span className="text-3xl font-extrabold text-primary/80 font-mono">
                        {step.number}
                      </span>
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-105 transition-transform">
                        {step.icon === "Eye" && <Eye className="size-5" />}
                        {step.icon === "Activity" && <Activity className="size-5" />}
                        {step.icon === "LineChart" && <LineChart className="size-5" />}
                      </div>
                    </div>
                    <CardTitle className="text-xl font-bold leading-snug text-foreground">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-3 pt-0">
                    <p className="text-sm font-medium text-foreground/90 leading-relaxed">
                      {step.summary}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.detail}
                    </p>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-2">
                  <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                    <span>Active Integration Step</span>
                    <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom CTA Card */}
        <div className="mt-12 lg:mt-16 p-8 rounded-xl bg-secondary/50 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="text-lg font-bold text-secondary-foreground mb-1">
              Ready to integrate process verification?
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak with our engineering specialists about validation, installation, and lifecycle support.
            </p>
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto">
              <Link to="/ContactPage">
                Get in Touch
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full md:w-auto">
              <Link to="/SolutionsPage">
                Explore Solutions
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}